import React, { useState, useEffect, useRef } from "react";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { computeQuoteDiscounts } from "../lib/quoteDiscounts";
import {
  Loader2, CheckCircle, FileText, PenTool, Check, Phone, Mail, MapPin, Download, Layers, Shield, Wind, ChevronDown, ChevronUp
} from "lucide-react";
import { saveAs } from "file-saver";
import { CoverPageDisplay } from "../components/quote-builder/CoverPageDisplay";
import { RichTextContent } from "../components/ui/RichTextEditor";
import { resolveImageUrl } from "../components/quote-templates/ContentBlocks";
import { BeforeAfterDisplay } from "../components/quote-templates/BeforeAfterDisplay";
import { applyMargin, applyProfit } from "../utils/pricing";
import { applyDeckingWorstCase } from "../lib/deckingWorstCase";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Calculate per-item total (same formula as QuoteBuilder)
const calcItemTotal = (it) => {
  const qty = parseFloat(it.quantity) || 0;
  const price = parseFloat(it.unit_price) || 0;
  const margin = parseFloat(it.margin) || 0;
  const waste = parseFloat(it.waste_factor) || 0;
  const effectiveQty = qty * (1 + waste / 100);
  return effectiveQty * price * (1 + margin / 100);
};

function CollapsibleTermsPublic({ terms }) {
  const [isOpen, setIsOpen] = useState(false);
  if (!terms) return null;

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="portal-terms-and-conditions">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
        data-testid="portal-terms-toggle"
      >
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#0F4C81]" />
          <span className="font-semibold text-slate-800">Terms & Conditions</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{isOpen ? "Collapse" : "Read before signing"}</span>
          {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {isOpen && (
        <CardContent className="px-6 pb-6 pt-0 border-t border-slate-100">
          <div className="max-h-[500px] overflow-y-auto pr-2 mt-4">
            <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed" data-testid="portal-terms-content">{terms}</div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function PublicQuoteView() {
  const { quoteId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [structureSelections, setStructureSelections] = useState({});
  const [signatureMethod, setSignatureMethod] = useState("type");
  const [typedSignature, setTypedSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  // True only for the signing that happened in THIS page session. Used to
  // pick the "in a moment" wording vs the calmer copy a customer sees when
  // they reopen the link later and we still haven't countersigned.
  const [justSigned, setJustSigned] = useState(false);
  const [customerAnswers, setCustomerAnswers] = useState({});
  const canvasRef = useRef(null);
  const signatureCardRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Set a meaningful title so iMessage/SMS link previews show "Tides Team Quote — Q-2024-005"
  const companyName = data?.quote?.company_name || data?.company?.name || "Tides Team Exteriors";
  const quoteNum = data?.quote?.quote_number;
  useDocumentTitle(quoteNum ? `${companyName} Quote — ${quoteNum}` : `${companyName} Quote`);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/quotes/${quoteId}`);
        setData(res.data);
        setSigned(!!res.data.quote.customer_signature?.signature);
        // Pre-select addons that are explicitly marked default_selected.
        // Default is OFF — customer has to opt in by checking each one.
        const alreadySigned = !!res.data.quote.customer_signature?.signature;
        if (!alreadySigned) {
          const presel = {};
          (res.data.quote.pages || []).forEach(p => {
            if (p.page_type === "addon" && p.enabled !== false) {
              if (p.content?.default_selected === true) presel[p.id] = true;
            }
          });
          setSelectedAddons(prev => ({ ...presel, ...prev }));
        } else {
          // After signing, hydrate the saved selection so the Signed Summary
          // panel + read-only pages reflect exactly what was approved.
          const savedAddons = res.data.quote.selected_addons || [];
          const addonMap = {};
          savedAddons.forEach(id => { addonMap[id] = true; });
          setSelectedAddons(addonMap);

          const savedOptions = res.data.quote.selected_options
            || (res.data.quote.selected_option ? [res.data.quote.selected_option] : []);
          // Map option id → its structure group key so the read-only view
          // knows which option is highlighted per structure. If we can't
          // find a group, fall back to keying by the option id itself.
          const pages = res.data.quote.pages || [];
          const groups = [];
          let currentGroup = { structure: null, options: [] };
          pages.filter(p => p.enabled !== false).forEach(page => {
            if (page.page_type === "structure") {
              if (currentGroup.options.length > 0 || currentGroup.structure) groups.push(currentGroup);
              currentGroup = { structure: page, options: [] };
            } else if (page.page_type === "option") {
              currentGroup.options.push(page);
            }
          });
          if (currentGroup.options.length > 0 || currentGroup.structure) groups.push(currentGroup);
          const structMap = {};
          savedOptions.forEach(optId => {
            const g = groups.find(gr => gr.options.some(o => o.id === optId));
            const key = g ? (g.structure?.id || g.options[0]?.id || optId) : optId;
            structMap[key] = optId;
          });
          setStructureSelections(structMap);
        }
      } catch (err) {
        if (err.response?.status === 410 && err.response?.data?.detail?.error === "expired") {
          setExpired(err.response.data.detail);
        } else {
          setError(err.response?.status === 404 ? "Quote not found" : "Failed to load quote");
        }
      }
      setLoading(false);
    };
    load();
  }, [quoteId]);

  // While the customer is signed but we haven't countersigned yet, poll so
  // the pay button appears on its own — no manual refresh. Stops the moment
  // the state moves on, so a quote sitting unpaid for days isn't polling.
  const depositState = data?.deposit?.state || "";
  useEffect(() => {
    if (depositState !== "awaiting_countersign") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/quotes/${quoteId}`);
        if (!cancelled) setData(res.data);
      } catch { /* keep polling; a transient failure shouldn't kill the page */ }
    };
    const timer = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [depositState, quoteId]);


  // Build structure groups from pages
  const buildStructureGroups = (pages) => {
    const groups = [];
    let currentGroup = { structure: null, options: [], addons: [] };
    const standaloneAddons = [];

    (pages || []).filter(p => p.enabled !== false).forEach(page => {
      if (page.page_type === "structure") {
        if (currentGroup.options.length > 0 || currentGroup.structure) {
          groups.push(currentGroup);
        }
        currentGroup = { structure: page, options: [], addons: [] };
      } else if (page.page_type === "option") {
        currentGroup.options.push(page);
      } else if (page.page_type === "addon") {
        if (currentGroup.structure || currentGroup.options.length > 0) {
          currentGroup.addons.push(page);
        } else {
          standaloneAddons.push(page);
        }
      }
    });
    if (currentGroup.options.length > 0 || currentGroup.structure) {
      groups.push(currentGroup);
    }
    return { groups, standaloneAddons };
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => ({ ...prev, [addonId]: !prev[addonId] }));
  };

  const handleStructureSelection = (structureId, optionId) => {
    setStructureSelections(prev => ({ ...prev, [structureId]: optionId }));
  };

  // Check if this is a template-based quote with page-level pricing
  const hasPagePricing = data?.quote?.pages?.some(
    p => (p.page_type === "option" || p.page_type === "addon") && p.enabled !== false
  );

  // Calculate live total for template-based quotes.
  // Rule: Customer Total = selected option + selected addons.
  // Structure pages are internal-only — the option's set price already rolls
  // those shared costs in. Matches services/quote_pricing.py::calc_page_total
  // and PricingTab.js summary math.
  const computeOptionFinal = (group, opt, quoteObj) => {
    const c = opt.content || {};
    const optMkp = parseFloat(c.price_markup_pct) || 0;
    const optAdj = parseFloat(c.price_adjustment) || 0;
    const manual = parseFloat(c.base_price || c.manual_price) || 0;
    if (manual > 0) {
      return manual + (manual * optMkp / 100) + optAdj;
    }
    const oItems = c.line_items || [];
    const oSub = oItems.reduce((s, it) => s + calcItemTotal(it), 0);
    const sharedItems = group.structure?.content?.line_items || [];
    const sharedSub = sharedItems.reduce((s, it) => s + calcItemTotal(it), 0);
    const profitPct = parseFloat(quoteObj?.profit_margin_pct) || 0;
    const taxPct = parseFloat(quoteObj?.vendor_tax_pct) || 0;
    const mode = quoteObj?.pricing_mode === "markup" ? "markup" : "margin";
    const totalRaw = oSub + sharedSub;
    const withMargin = applyProfit(totalRaw, profitPct, mode);
    const optTax = totalRaw * (taxPct / 100);
    const optBase = withMargin + optTax;
    return optBase + (optBase * optMkp / 100) + optAdj;
  };

  const computeAddonFinal = (addon, quoteObj) => {
    // Add-ons never inherit the quote-level profit_margin_pct. They keep only
    // their own per-addon markup + adjustment + tax (either a per-addon
    // tax_rate on manual-price addons, or the quote-level vendor_tax_pct on
    // line-item addons for consistency with the summary panel).
    const c = addon.content || {};
    const addonMkp = parseFloat(c.price_markup_pct) || 0;
    const addonAdj = parseFloat(c.price_adjustment) || 0;
    const manual = parseFloat(c.base_price || c.manual_price) || 0;
    if (manual > 0) {
      return manual + (manual * addonMkp / 100) + addonAdj;
    }
    const aItems = c.line_items || [];
    const aSub = aItems.reduce((s, it) => s + calcItemTotal(it), 0);
    const taxPct = parseFloat(quoteObj?.vendor_tax_pct) || 0;
    const addonTax = aSub * (taxPct / 100);
    const addonBase = aSub + addonTax;
    return addonBase + (addonBase * addonMkp / 100) + addonAdj;
  };

  const getPageBasedTotal = () => {
    if (!data) return { subtotal: 0, tax: 0, total: 0 };
    const pages = data.quote.pages || [];
    const { groups, standaloneAddons } = buildStructureGroups(pages);

    let subtotal = 0;
    const tax = 0;

    // For each structure group, add ONLY the customer's selected option price
    groups.forEach((group) => {
      const structureKey = group.structure?.id || group.options[0]?.id || "default";
      const selectedOptId = structureSelections[structureKey];
      if (!selectedOptId) return;
      const opt = group.options.find(o => o.id === selectedOptId);
      if (!opt) return;
      subtotal += computeOptionFinal(group, opt, data.quote);
    });

    // Add selected addon pages — same rule
    [...(pages.filter(p => p.page_type === "addon")), ...standaloneAddons].forEach(addon => {
      if (!selectedAddons[addon.id]) return;
      subtotal += computeAddonFinal(addon, data.quote);
    });

    const { totalRemoved, rows: discountRows } = computeQuoteDiscounts(subtotal + tax, data?.quote);
    const finalTotal = Math.max(0, subtotal + tax - totalRemoved);
    return {
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      discount: totalRemoved.toFixed(2),
      discount_label: data?.quote?.discount_label || "",
      discount_rows: discountRows,
      total: finalTotal.toFixed(2),
    };
  };

  // Calculate live total for flat (non-template) quotes
  const getFlatTotal = () => {
    if (!data) return { subtotal: 0, discount: 0, tax: 0, total: 0 };
    const p = data.quote.pricing;
    let subtotal = p.base.subtotal;
    let discount = p.base.discount;
    let taxVal = p.base.tax;
    let total = p.base.total;

    p.addons.forEach(addon => {
      if (selectedAddons[addon.id]) {
        subtotal += addon.subtotal;
        discount += addon.discount;
        taxVal += addon.tax;
        total += addon.total;
      }
    });

    const { totalRemoved: quoteDiscount, rows: discountRows } = computeQuoteDiscounts(total, data?.quote);
    total = Math.max(0, total - quoteDiscount);
    discount += quoteDiscount;

    return {
      subtotal: subtotal.toFixed(2),
      discount: discount.toFixed(2),
      discount_label: data?.quote?.discount_label || "",
      discount_rows: discountRows,
      tax: taxVal.toFixed(2),
      total: total.toFixed(2),
    };
  };

  // Signature canvas handlers
  const startDraw = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.strokeStyle = "#0F4C81";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const onDraw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSign = async () => {
    const name = signerName || typedSignature;
    if (!name) { toast.error("Please enter your name"); return; }

    let signatureData;
    if (signatureMethod === "type") {
      const c = document.createElement("canvas");
      c.width = 400; c.height = 100;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 400, 100);
      ctx.font = 'italic 36px "Georgia", serif';
      ctx.fillStyle = "#0F4C81";
      ctx.fillText(typedSignature, 20, 60);
      signatureData = c.toDataURL();
    } else {
      if (!canvasRef.current) return;
      signatureData = canvasRef.current.toDataURL();
    }

    setSigning(true);
    try {
      const selectedIds = Object.keys(selectedAddons).filter(id => selectedAddons[id]);
      const selectedOptionIds = Object.values(structureSelections).filter(Boolean);

      // Build the itemized breakdown the customer sees on screen so the
      // notification email + audit trail can render the exact line items.
      const pages = data?.quote?.pages || [];
      const { groups: sGroups, standaloneAddons: sStandalone } = buildStructureGroups(pages);
      const clientBreakdown = [];
      sGroups.forEach(group => {
        const structureKey = group.structure?.id || group.options[0]?.id || "default";
        const selOptId = structureSelections[structureKey];
        if (!selOptId) return;
        const opt = group.options.find(o => o.id === selOptId);
        if (!opt) return;
        const label = opt.content?.title || opt.title || "Option";
        const amount = Number(computeOptionFinal(group, opt, data.quote).toFixed(2));
        clientBreakdown.push({ kind: "option", label, amount });
      });
      // Include EVERY selected addon (mandatory + optional). Also flag
      // mandatory ones so the email + PDF can label them consistently.
      [...(pages.filter(p => p.page_type === "addon")), ...sStandalone].forEach(addon => {
        if (!selectedAddons[addon.id]) return;
        const label = addon.content?.title || addon.title || "Add-on";
        const amount = Number(computeAddonFinal(addon, data.quote).toFixed(2));
        clientBreakdown.push({ kind: "addon", label, amount, mandatory: !!addon.content?.mandatory });
      });
      // Any quote-level discount lines applied to the subtotal
      const subtotalForDisc = clientBreakdown.reduce((s, r) => s + (r.kind === "discount" ? 0 : r.amount), 0);
      const { rows: discRows } = computeQuoteDiscounts(subtotalForDisc, data.quote);
      (discRows || []).forEach(row => {
        clientBreakdown.push({
          kind: "discount",
          label: row.label || "Discount",
          amount: Number((row.removed || 0).toFixed(2)),
        });
      });

      await axios.post(`${API_URL}/public/quotes/${quoteId}/sign`, {
        signature: signatureData,
        name,
        selected_addons: selectedIds,
        selected_options: selectedOptionIds,
        selected_option: selectedOptionIds[0] || null,
        client_total: Number(totals.total),
        client_breakdown: clientBreakdown,
        answers: customerAnswers,
      });
      // Refetch before flipping into the signed view. Without this, `data`
      // still holds the pre-sign payload: `deposit.state` stays "none" so
      // the State A card never renders, the Signed Summary shows a stale
      // Approved Total, and the countersign poll (keyed on deposit.state)
      // never starts — all of which only corrected on a manual reload.
      setJustSigned(true);
      try {
        const fresh = await axios.get(`${API_URL}/public/quotes/${quoteId}`);
        setData(fresh.data);
      } catch {
        // Signing succeeded even if the refetch didn't; the poll below and
        // the next page load will catch up.
      }
      setSigned(true);
      toast.success("Quote signed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign quote");
    }
    setSigning(false);
  };

  const handleDownloadPDF = () => {
    const url = `${API_URL}/public/quotes/${quoteId}/pdf`;
    console.log("[PDF] Public download triggered, URL:", url);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } }, 30000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
      </div>
    );
  }

  if (expired) {
    const validDate = expired.valid_until ? new Date(expired.valid_until + "T12:00:00").toLocaleDateString() : "";
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full" data-testid="quote-expired-card">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-amber-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">This quote has expired</h2>
            <p className="text-slate-500 text-sm leading-relaxed">
              {expired.first_name ? `${expired.first_name}, ` : ""}quote {expired.quote_number}{validDate ? ` was valid through ${validDate}` : ""}. Pricing can shift with material costs, so give us a quick shout and we'll refresh it for you — usually same day.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              {expired.company_phone && (
                <a href={`tel:${expired.company_phone}`} className="inline-flex items-center justify-center h-11 rounded-lg bg-[#0F4C81] text-white font-semibold text-sm hover:bg-[#0D4270]" data-testid="expired-call-btn">Call {expired.company_phone}</a>
              )}
              {expired.company_email && (
                <a href={`mailto:${expired.company_email}?subject=${encodeURIComponent(`Please reopen quote ${expired.quote_number}`)}`} className="inline-flex items-center justify-center h-11 rounded-lg border border-[#0F4C81] text-[#0F4C81] font-semibold text-sm hover:bg-slate-50" data-testid="expired-email-btn">Email us to reopen it</a>
              )}
            </div>
            <p className="mt-6 text-xs text-slate-400">{expired.company_name}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">{error}</h2>
            <p className="text-slate-500">Please check the link or contact the company.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { quote, contact, project, company } = data;
  const alreadySigned = signed || !!quote.customer_signature?.signature;
  const totals = hasPagePricing ? getPageBasedTotal() : getFlatTotal();
  const { groups: structureGroups, standaloneAddons } = buildStructureGroups(quote.pages || []);
  const addonPages = (quote.pages || []).filter(p => p.page_type === "addon" && p.enabled !== false);

  // Mobile compact layout: renders option/add-on cards in a 2-column grid on
  // phones so tall quotes aren't a long, skinny scroll. Global toggle lives on
  // template_settings.compact_mobile_layout; individual pages can override via
  // page.mobile_layout ("auto" | "single" | "double").
  const compactMobileDefault = !!quote.template_settings?.compact_mobile_layout;
  const isDoubleColumn = (page) => {
    const ml = page?.mobile_layout || "auto";
    if (ml === "double") return true;
    if (ml === "single") return false;
    return compactMobileDefault;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Slim action bar */}
      <div className="bg-slate-900 text-white py-2.5 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src="/logo-dark.png"
              alt={company.name}
              className="h-8 w-auto rounded-sm bg-slate-800 px-1 py-0.5 flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="text-sm font-medium text-slate-300 truncate">{company.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`${API_URL}/public/quotes/${quoteId}/pdf`}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center h-9 px-4 rounded-md bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors shadow-sm"
              data-testid="portal-download-pdf"
            >
              <Download className="w-4 h-4 mr-1.5" />Download PDF
            </a>
            <Badge className="bg-white/20 text-white border-white/30 text-xs" data-testid="quote-status-badge">
              {quote.admin_signature?.signature && alreadySigned ? "Fully Executed" : alreadySigned ? "Approved" : "Awaiting Signature"}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6 pb-24 sm:pb-6">
        {/* Cover Page */}
        {(() => {
          const coverPage = (quote.pages || []).find(p => p.page_type === "cover");
          const cc = coverPage?.content || {};
          return (
            <CoverPageDisplay
              headline={cc.headline || "PROPOSAL"}
              subheadline={cc.subheadline || ""}
              logoUrl={cc.logo_url || ""}
              bgImageUrl={cc.bg_image_url || ""}
              bgColor={cc.bgColor || "#0F4C81"}
              companyName={company.name || ""}
              companyPhone={company.phone || ""}
              companyEmail={company.email || ""}
              companyAddress={company.address || ""}
              contactName={contact.name || ""}
              projectAddress={project.address || ""}
              quoteNumber={quote.quote_number ? `Quote #${quote.quote_number}` : ""}
              showLogo={cc.show_logo !== false}
              showDate={cc.show_date !== false}
              showQuoteNumber={cc.show_quote_number !== false}
              showContactInfo={cc.show_contact_info !== false}
              showCompanyInfo={cc.show_company_info !== false}
            />
          );
        })()}


        {/* ===== RENDER ALL PAGES IN ARRAY ORDER ===== */}
        {(() => {
          const enabledPages = (quote.pages || []).filter(p => p.enabled !== false && p.page_type !== "cover" && p.page_type !== "signature");
          const segments = [];
          let idx = 0;
          while (idx < enabledPages.length) {
            const pg = enabledPages[idx];
            if (pg.page_type === "structure" || pg.page_type === "option") {
              const grp = { structure: pg.page_type === "structure" ? pg : null, options: [], addons: [] };
              if (pg.page_type === "option") grp.options.push(pg);
              idx++;
              while (idx < enabledPages.length) {
                const nx = enabledPages[idx];
                if (nx.page_type === "option") { grp.options.push(nx); idx++; }
                else if (nx.page_type === "addon") { grp.addons.push(nx); idx++; }
                else break;
              }
              if (grp.options.length > 0) segments.push({ type: "group", data: grp });
            } else if (pg.page_type === "addon") {
              segments.push({ type: "addon", data: pg }); idx++;
            } else if (pg.page_type === "financing") {
              segments.push({ type: "financing", data: pg }); idx++;
            } else {
              segments.push({ type: "content", data: pg }); idx++;
            }
          }
          return segments.map((seg, si) => {
            if (seg.type === "content") {
              const page = seg.data;
              return (
                <Card key={page.id}>
                  <CardHeader className={page.page_type === "custom" ? "text-center" : ""}><CardTitle className="text-base">{page.content?.title || page.title}</CardTitle></CardHeader>
                  <CardContent>
                    {page.page_type === "text" && (
                      <div>
                        {page.content?.body && <RichTextContent content={page.content.body} className="text-slate-600" />}
                        {page.content?.sections?.map((s, i) => (
                          <div key={i} className="mt-4">
                            <h4 className="font-semibold text-slate-700 mb-1">{s.title}</h4>
                            <RichTextContent content={s.body} className="text-slate-600" />
                          </div>
                        ))}
                      </div>
                    )}
                    {(page.page_type === "custom" || (page.content?.blocks?.length > 0 && !["cover", "text", "structure", "pricing", "signature", "financing", "option", "addon"].includes(page.page_type))) && (
                      <div className="space-y-4">
                        {page.content?.blocks?.map((block, bi) => (
                          <div key={bi}>
                            {block.type === "text" && (() => { const t = typeof block.content === "string" ? block.content : (block.content?.html || ""); return t ? <p className="text-slate-600 whitespace-pre-wrap">{t}</p> : null; })()}
                            {block.type === "richtext" && <RichTextContent content={block.content} className="text-slate-600" />}
                            {block.type === "html" && (() => { let h = typeof block.content === "string" ? block.content : (block.content?.html || ""); h = h.replace(/https?:\/\/[^"'\s]+?(?=\/api\/uploads\/)/g, window.location.origin); return <div dangerouslySetInnerHTML={{ __html: h }} />; })()}
                            {block.type === "iframe" && block.url && <div className="rounded-lg overflow-hidden border"><iframe src={block.url} title={block.title || ""} width="100%" height={block.height || 700} frameBorder="0" className="w-full" style={{ minHeight: block.height || 700 }} /></div>}
                            {block.type === "image" && block.url && (() => { const a = block.textAlign || "center"; const w = block.width || "full"; const ws = w === "full" ? "100%" : `${w}%`; const wc = a === "left" ? "flex justify-start" : a === "right" ? "flex justify-end" : "flex justify-center"; return <div className={wc}><img src={resolveImageUrl(block.url)} alt={block.alt || ""} className="rounded-lg object-contain" style={{ width: ws, height: "auto" }} /></div>; })()}
                            {block.type === "before_after" && <BeforeAfterDisplay block={block} />}
                            {block.type === "video" && block.url && (() => {
                              const url = resolveImageUrl(block.url);
                              let embed = "";
                              if (url.includes("youtube.com/watch")) embed = url.replace("watch?v=", "embed/");
                              else if (url.includes("youtu.be/")) embed = `https://www.youtube.com/embed/${url.split("youtu.be/")[1]}`;
                              else if (/vimeo\.com\/\d+/.test(url)) embed = `https://player.vimeo.com/video/${url.split("vimeo.com/")[1].split(/[?#]/)[0]}`;
                              const direct = !embed && (/\.(mp4|mov|webm|m4v|ogv)(\?|$)/i.test(url) || url.includes("/api/uploads/"));
                              return (
                                <div className="space-y-1" data-testid="public-quote-video-block">
                                  {direct ? (
                                    <video controls playsInline src={url} className="w-full rounded-lg bg-black border" preload="metadata" />
                                  ) : (
                                    <div className="aspect-video rounded-lg overflow-hidden bg-slate-100"><iframe src={embed || url} title="Video" className="w-full h-full border-0" allowFullScreen /></div>
                                  )}
                                  {block.caption && <p className="text-xs text-slate-500 text-center">{block.caption}</p>}
                                </div>
                              );
                            })()}
                            {block.type === "columns" && (() => { const cc = block.columns || 2; const cd = block.column_data || []; return (<div className={`grid gap-4 ${cc === 2 ? "grid-cols-2" : cc === 3 ? "grid-cols-3" : "grid-cols-4"}`}>{cd.slice(0, cc).map((col, ci) => (<div key={ci} className="text-sm text-slate-600"><RichTextContent content={col.content} /></div>))}</div>); })()}
                            {block.type === "table" && typeof block.content === "object" && block.content?.headers && (
                              <table className="w-full border border-slate-200 text-sm"><thead><tr className="bg-[#0F4C81] text-white">{block.content.headers.map((h, hi) => <th key={hi} className="px-3 py-2 text-left">{h}</th>)}</tr></thead><tbody>{(block.content.rows || []).map((row, ri) => <tr key={ri} className="border-b border-slate-100">{row.map((cell, ci) => <td key={ci} className="px-3 py-2">{cell}</td>)}</tr>)}</tbody></table>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            }
            if (seg.type === "financing") {
              const fc = seg.data.content || {};
              return (
                <Card key={seg.data.id} data-testid={`portal-financing-page-${si}`}>
                  <CardContent className="p-5 sm:p-8">
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">{fc.title || "Financing Options"}</h2>
                    {fc.intro && <p className="text-slate-600 mb-6">{fc.intro}</p>}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                      {/* HFS Card */}
                      <div className="rounded-2xl overflow-hidden border-2 border-emerald-200 bg-gradient-to-b from-emerald-50/80 to-white shadow-sm">
                        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-4 text-center">
                          <p className="text-white font-bold text-lg">HFS Financial</p>
                          <p className="text-emerald-100 text-sm mt-0.5">Funded Directly to <span className="font-bold underline">YOU</span></p>
                        </div>
                        <div className="p-5 space-y-3">
                          <p className="text-sm text-slate-600 leading-relaxed">{fc.hfs_desc || "Loan funds are deposited directly into your bank account."}</p>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-semibold">Soft Credit Pull</span>
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-semibold">Flexible Terms</span>
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-semibold">Quick Approval</span>
                          </div>
                          {fc.hfs_link && (
                            <a href={fc.hfs_link} target="_blank" rel="noopener noreferrer" data-testid="hfs-prequal-btn"
                              className="block w-full text-center px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm transition-all hover:shadow-lg mt-2">
                              Get Pre-Qualified with HFS
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Wisetack Card */}
                      <div className="rounded-2xl overflow-hidden border-2 border-blue-200 bg-gradient-to-b from-blue-50/80 to-white shadow-sm">
                        <div className="bg-gradient-to-r from-[#0F4C81] to-[#0D4270] px-5 py-4 text-center">
                          <p className="text-white font-bold text-lg">Wisetack</p>
                          <p className="text-blue-200 text-sm mt-0.5">Funded Directly to <span className="font-bold underline">YOUR CONTRACTOR</span></p>
                        </div>
                        <div className="p-5 space-y-3">
                          <p className="text-sm text-slate-600 leading-relaxed">{fc.wisetack_desc || "Funds go directly to your contractor. Easy monthly payments."}</p>
                          <div className="flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-semibold">Soft Credit Pull</span>
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-semibold">Instant Decision</span>
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-semibold">Easy Payments</span>
                          </div>
                          {fc.wisetack_link && (
                            <a href={fc.wisetack_link} target="_blank" rel="noopener noreferrer" data-testid="wisetack-prequal-btn"
                              className="block w-full text-center px-5 py-3 bg-[#0F4C81] hover:bg-[#0D4270] text-white font-bold rounded-xl text-sm transition-all hover:shadow-lg mt-2">
                              Get Pre-Qualified with Wisetack
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">How does it work?</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                        <div className="space-y-1">
                          <div className="w-8 h-8 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-sm font-bold mx-auto">1</div>
                          <p className="text-xs font-medium text-slate-700">Pre-Qualify</p>
                          <p className="text-[10px] text-slate-500">Quick soft pull, no credit impact</p>
                        </div>
                        <div className="space-y-1">
                          <div className="w-8 h-8 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-sm font-bold mx-auto">2</div>
                          <p className="text-xs font-medium text-slate-700">Pick Your Terms</p>
                          <p className="text-[10px] text-slate-500">Choose the plan that fits your budget</p>
                        </div>
                        <div className="space-y-1">
                          <div className="w-8 h-8 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-sm font-bold mx-auto">3</div>
                          <p className="text-xs font-medium text-slate-700">Get Started</p>
                          <p className="text-[10px] text-slate-500">We begin your project right away</p>
                        </div>
                      </div>
                    </div>

                    {fc.blocks?.map((blk, bi) => (<div key={bi}>{blk.type === "html" && <div dangerouslySetInnerHTML={{ __html: typeof blk.content === "string" ? blk.content : (blk.content?.html || "") }} />}{blk.type === "richtext" && <RichTextContent content={blk.content} />}{blk.type === "image" && blk.url && (() => { const a = blk.textAlign || "center"; const w = blk.width || "full"; const ws = w === "full" ? "100%" : `${w}%`; const wc = a === "left" ? "flex justify-start" : a === "right" ? "flex justify-end" : "flex justify-center"; return <div className={wc}><img src={resolveImageUrl(blk.url)} alt="" className="rounded-lg object-contain" style={{ width: ws, height: "auto" }} /></div>; })()}{blk.type === "before_after" && <BeforeAfterDisplay block={blk} />}</div>))}

                    {fc.show_calculator !== false && (
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-2">Payment Calculator</p>
                        <div className="rounded-xl overflow-hidden border border-slate-200">
                          <iframe src="https://wisetack.us/#/payment_calculator?type=HS" title="Payment Calculator" width="100%" height="600" frameBorder="0" className="w-full" style={{ minHeight: 500 }} />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            }
            if (seg.type === "group") {
              const group = seg.data;
              if (group.options.length === 0) return null;
              const sKey = group.structure?.id || group.options[0]?.id || `default_${si}`;
              const sTitle = group.structure?.content?.title || "Select Your Option";
              const sharedItems = group.structure?.content?.line_items || [];
              const sharedTotal = sharedItems.reduce((sum, it) => sum + calcItemTotal(it), 0);
              const selOptId = structureSelections[sKey];
              // Per-group mobile layout: honour structure page override first,
              // else fall back to the template's compact_mobile_layout flag.
              const isDouble = isDoubleColumn(group.structure);
              const optionsWrapperClass = isDouble
                ? "grid grid-cols-2 gap-2 sm:gap-3"
                : "space-y-3";
              const addonsWrapperClass = isDouble
                ? "grid grid-cols-2 gap-2 sm:gap-3"
                : "space-y-3";
              return (
                <div key={sKey} className="space-y-4">
                  <Card className="overflow-hidden" data-testid={`portal-structure-${si}`}>
                    <div className="bg-gradient-to-r from-[#0F4C81] to-[#0F4C81]/90 px-6 py-4"><div className="flex items-center gap-2"><Layers className="w-5 h-5 text-white" /><h3 className="text-lg font-bold text-white">{sTitle}</h3></div><p className="text-sm text-blue-200 mt-1">Choose one option below</p></div>
                    <CardContent className="p-5 space-y-4">
                      {group.structure?.content?.notes && <div className="bg-slate-800 text-white rounded-lg p-4"><p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1">Important Information</p><RichTextContent content={applyDeckingWorstCase(group.structure.content.notes, data?.quote?.roof_total_sqft)} className="text-sm leading-relaxed text-slate-200 [&_p]:text-slate-200" /></div>}
                      <div className={optionsWrapperClass} data-testid={`portal-group-options-${si}`}>
                        {group.options.map(opt => {
                          const c = opt.content || {};
                          const isSel = selOptId === opt.id;
                          const oItems = c.line_items || [];
                          const oManual = parseFloat(c.base_price || c.manual_price) || 0;
                          const oSub = oManual > 0 ? oManual : (oItems.length > 0 ? oItems.reduce((sum, it) => sum + calcItemTotal(it), 0) : 0);
                          let oTotal;
                          let subtotal;
                          if (oManual > 0) {
                            // Manual sells-for = final; only markup/adj applied
                            const optMkp = parseFloat(c.price_markup_pct) || 0;
                            const optAdj = parseFloat(c.price_adjustment) || 0;
                            subtotal = oSub;
                            oTotal = oSub + (oSub * optMkp / 100) + optAdj;
                          } else {
                            const totalRaw = oSub + sharedTotal;
                            const profitPct = parseFloat(data?.quote?.profit_margin_pct) || 0;
                            const mode = data?.quote?.pricing_mode === "markup" ? "markup" : "margin";
                            const withMargin = applyProfit(totalRaw, profitPct, mode);
                            const taxPct = parseFloat(data?.quote?.vendor_tax_pct) || 0;
                            const tax = totalRaw * (taxPct / 100);
                            subtotal = withMargin + tax;
                            const optMkp = parseFloat(c.price_markup_pct) || 0;
                            const optAdj = parseFloat(c.price_adjustment) || 0;
                            oTotal = subtotal + (subtotal * optMkp / 100) + optAdj;
                          }
                          const cashP = oTotal * 0.98;
                          return (
                            <div key={opt.id} className={`rounded-xl border-2 cursor-pointer transition-all ${isSel ? "border-[#0F4C81] bg-[#0F4C81]/5 shadow-md" : "border-slate-200 hover:border-slate-300"} ${alreadySigned ? "pointer-events-none opacity-70" : ""}`}
                              onClick={() => !alreadySigned && setStructureSelections(prev => ({ ...prev, [sKey]: opt.id }))} data-testid={`portal-option-page-${opt.id}`}>
                              <div className={`flex items-center gap-2 sm:gap-3 ${isDouble ? "px-3 sm:px-4" : "px-4 sm:px-5"} pt-4`}>
                                <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSel ? "bg-[#0F4C81] border-[#0F4C81]" : "border-slate-300"}`}>{isSel && <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-white" />}</div>
                                <div className="flex-1 min-w-0"><p className={`font-bold text-slate-800 ${isDouble ? "text-sm sm:text-base" : "text-base sm:text-lg"}`}>{c.option_name || opt.title}</p>{c.option_tag && <Badge className="mt-1 text-[10px] sm:text-xs bg-blue-100 text-blue-700 border-blue-200">{c.option_tag}</Badge>}</div>
                              </div>
                              <div className={`${isDouble ? "px-3 sm:px-4" : "px-4 sm:px-5"} pb-4 pt-2`}>
                                {c.description && <RichTextContent content={c.description} className={`${isDouble ? "text-xs sm:text-sm" : "text-sm"} text-slate-500 mb-3`} />}
                                {c.warranty_badges?.length > 0 && <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-center mb-3">{c.warranty_badges.map((wb, wbi) => <Badge key={wbi} className="bg-slate-800 text-white border-slate-700 text-[10px] sm:text-xs px-2 sm:px-3 py-0.5 sm:py-1">{wb.label}: {wb.years}</Badge>)}</div>}
                                {oTotal > 0 && (
                                  <div className="text-center py-2">
                                    <p className={`${isDouble ? "text-lg sm:text-2xl" : "text-2xl sm:text-3xl"} font-extrabold ${isSel && parseFloat(totals.discount) > 0 ? "text-slate-400 line-through decoration-2" : "text-[#0F4C81]"}`}>${oTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    {isSel && parseFloat(totals.discount) > 0 && (
                                      <div className="mt-1 space-y-0.5" data-testid={`option-discount-block-${opt.id}`}>
                                        <p className={`${isDouble ? "text-[10px] sm:text-xs" : "text-xs"} uppercase tracking-wider text-rose-600 font-semibold`}>
                                          {(totals.discount_label || "Discount")}: −${parseFloat(totals.discount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </p>
                                        <p className={`${isDouble ? "text-xl sm:text-3xl" : "text-3xl sm:text-4xl"} font-extrabold text-emerald-600`}>${Math.max(0, oTotal - parseFloat(totals.discount)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                      </div>
                                    )}
                                    {data?.card_fee?.enabled ? (() => { const base = isSel && parseFloat(totals.discount) > 0 ? Math.max(0, oTotal - parseFloat(totals.discount)) : oTotal; const pct = Number(data.card_fee.pct) || 0; const cc = base + Math.round(Number((base * pct).toFixed(6))) / 100; return <p className={`${isDouble ? "text-xs sm:text-sm" : "text-sm"} text-slate-500 font-semibold mt-1`} data-testid={`option-card-price-${opt.id}`}>Credit card: ${cc.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (includes {pct}% card fee). Bank, cash or check: no fee.</p>; })() : (c.show_cash_discount !== false && <p className={`${isDouble ? "text-xs sm:text-sm" : "text-sm"} text-emerald-600 font-semibold mt-1`}>Cash/Check: ${(isSel && parseFloat(totals.discount) > 0 ? (Math.max(0, oTotal - parseFloat(totals.discount))) * 0.98 : cashP).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>)}
                                  </div>
                                )}
                                {c.scope && c.show_scope !== false && <div className={`bg-slate-50 rounded-lg ${isDouble ? "p-2 sm:p-3" : "p-3 sm:p-4"} mt-2`}><p className={`font-semibold text-slate-700 ${isDouble ? "text-[10px] sm:text-xs" : "text-xs"} uppercase tracking-wider mb-2`}>Scope of Work</p><RichTextContent content={c.scope} className={`${isDouble ? "text-xs sm:text-sm" : "text-sm"} text-slate-600 leading-relaxed`} /></div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  {group.addons.length > 0 && (
                    <Card><CardHeader><CardTitle>Optional Add-ons</CardTitle><p className="text-sm text-slate-500">Select any additional services:</p></CardHeader>
                      <CardContent><div className={addonsWrapperClass} data-testid={`portal-group-addons-${si}`}>{group.addons.map(addon => {
                        const c = addon.content || {}; const isSel = !!selectedAddons[addon.id]; const aItems = c.line_items || [];
                        const aManual = parseFloat(c.base_price || c.manual_price) || 0;
                        const addonMkp = parseFloat(c.price_markup_pct) || 0;
                        const addonAdj = parseFloat(c.price_adjustment) || 0;
                        let aTotal;
                        if (aManual > 0) {
                          aTotal = aManual + (aManual * addonMkp / 100) + addonAdj;
                        } else {
                          const aSub = aItems.length > 0 ? aItems.reduce((sum, it) => sum + calcItemTotal(it), 0) : 0;
                          aTotal = aSub + aSub * (parseFloat(c.tax_rate) || 0) / 100;
                          aTotal = aTotal + (aTotal * addonMkp / 100) + addonAdj;
                        }
                        return (<div key={addon.id} className={`rounded-xl border-2 cursor-pointer transition-all ${isSel ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 hover:border-slate-300"} ${alreadySigned ? "pointer-events-none opacity-70" : ""}`}
                          onClick={() => !alreadySigned && toggleAddon(addon.id)} data-testid={`portal-addon-page-${addon.id}`}>
                          <div className={`flex items-center gap-2 sm:gap-3 ${isDouble ? "px-3 sm:px-4" : "px-4 sm:px-5"} pt-4`}><div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSel ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>{isSel && <Check className="w-3.5 h-3.5 text-white" />}</div><p className={`font-bold text-slate-800 ${isDouble ? "text-sm sm:text-base" : "text-base sm:text-lg"}`}>{c.addon_name || addon.title}</p></div>
                          <div className={`${isDouble ? "px-3 sm:px-4" : "px-4 sm:px-5"} pb-4 pt-2`}>{c.description && <RichTextContent content={c.description} className={`${isDouble ? "text-xs sm:text-sm" : "text-sm"} text-slate-500 mb-2`} />}<p className={`${isDouble ? "text-lg sm:text-xl" : "text-xl sm:text-2xl"} font-extrabold text-emerald-700 text-center`}>+${aTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p></div>
                        </div>);
                      })}</div></CardContent>
                    </Card>
                  )}
                </div>
              );
            }
            if (seg.type === "addon") {
              const addon = seg.data; const c = addon.content || {}; const isSel = !!selectedAddons[addon.id]; const aItems = c.line_items || [];
              const aManual = parseFloat(c.base_price || c.manual_price) || 0;
              const addonMkp = parseFloat(c.price_markup_pct) || 0;
              const addonAdj = parseFloat(c.price_adjustment) || 0;
              let aTotal;
              if (aManual > 0) {
                aTotal = aManual + (aManual * addonMkp / 100) + addonAdj;
              } else {
                const aSub = aItems.length > 0 ? aItems.reduce((sum, it) => sum + calcItemTotal(it), 0) : 0;
                aTotal = aSub + aSub * (parseFloat(c.tax_rate) || 0) / 100;
                aTotal = aTotal + (aTotal * addonMkp / 100) + addonAdj;
              }
              return (<div key={addon.id} className={`rounded-xl border-2 cursor-pointer transition-all ${isSel ? "border-emerald-500 bg-emerald-50/50" : "border-slate-200 hover:border-slate-300"} ${alreadySigned ? "pointer-events-none opacity-70" : ""}`}
                onClick={() => !alreadySigned && toggleAddon(addon.id)}><div className="flex items-center gap-3 px-5 pt-4"><div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${isSel ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>{isSel && <Check className="w-4 h-4 text-white" />}</div><p className="font-bold text-slate-800 text-lg">{c.addon_name || addon.title}</p></div>
                <div className="px-5 pb-4 pt-2">{c.description && <RichTextContent content={c.description} className="text-sm text-slate-500 mb-2" />}<p className="text-2xl font-extrabold text-emerald-700 text-center">+${aTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p></div></div>);
            }
            return null;
          });
        })()}

        {/* Financing auto-inject if template has financing_offer but no financing page in the pages array */}
        {quote.template_settings?.financing_offer && !(quote.pages || []).some(p => p.page_type === "financing" && p.enabled !== false) && (
          <Card data-testid="portal-financing-section">
            <CardContent className="p-5 sm:p-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Financing Options</h2>
              <p className="text-slate-600 mb-6">We&apos;ve partnered with trusted lenders to offer you flexible financing options. Both start with a soft credit pull that won&apos;t impact your credit score.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                <div className="rounded-2xl overflow-hidden border-2 border-emerald-200 bg-gradient-to-b from-emerald-50/80 to-white shadow-sm">
                  <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-5 py-4 text-center">
                    <p className="text-white font-bold text-lg">HFS Financial</p>
                    <p className="text-emerald-100 text-sm mt-0.5">Funded Directly to <span className="font-bold underline">YOU</span></p>
                  </div>
                  <div className="p-5 space-y-3">
                    <p className="text-sm text-slate-600 leading-relaxed">Loan funds are deposited directly into your bank account. You manage the payments to your contractor on your terms.</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-semibold">Soft Credit Pull</span>
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-semibold">Flexible Terms</span>
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full text-xs font-semibold">Quick Approval</span>
                    </div>
                    <a href="https://www.hfsfinancial.net/promo/671a57b6111b95af7b6d0105" target="_blank" rel="noopener noreferrer" data-testid="hfs-prequal-btn-auto"
                      className="block w-full text-center px-5 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-sm transition-all hover:shadow-lg mt-2">
                      Get Pre-Qualified with HFS
                    </a>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden border-2 border-blue-200 bg-gradient-to-b from-blue-50/80 to-white shadow-sm">
                  <div className="bg-gradient-to-r from-[#0F4C81] to-[#0D4270] px-5 py-4 text-center">
                    <p className="text-white font-bold text-lg">Wisetack</p>
                    <p className="text-blue-200 text-sm mt-0.5">Funded Directly to <span className="font-bold underline">YOUR CONTRACTOR</span></p>
                  </div>
                  <div className="p-5 space-y-3">
                    <p className="text-sm text-slate-600 leading-relaxed">Funds go directly to your contractor. Easy monthly payments through the Wisetack app.</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-semibold">Soft Credit Pull</span>
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-semibold">Instant Decision</span>
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full text-xs font-semibold">Easy Payments</span>
                    </div>
                    <a href="https://wisetack.us/#/c93x7ox/prequalify" target="_blank" rel="noopener noreferrer" data-testid="wisetack-prequal-btn-auto"
                      className="block w-full text-center px-5 py-3 bg-[#0F4C81] hover:bg-[#0D4270] text-white font-bold rounded-xl text-sm transition-all hover:shadow-lg mt-2">
                      Get Pre-Qualified with Wisetack
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-4">
                <p className="text-sm font-semibold text-slate-700 mb-2">How does it work?</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
                  <div className="space-y-1">
                    <div className="w-8 h-8 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-sm font-bold mx-auto">1</div>
                    <p className="text-xs font-medium text-slate-700">Pre-Qualify</p>
                    <p className="text-[10px] text-slate-500">Quick soft pull, no credit impact</p>
                  </div>
                  <div className="space-y-1">
                    <div className="w-8 h-8 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-sm font-bold mx-auto">2</div>
                    <p className="text-xs font-medium text-slate-700">Pick Your Terms</p>
                    <p className="text-[10px] text-slate-500">Choose the plan that fits your budget</p>
                  </div>
                  <div className="space-y-1">
                    <div className="w-8 h-8 rounded-full bg-[#0F4C81] text-white flex items-center justify-center text-sm font-bold mx-auto">3</div>
                    <p className="text-xs font-medium text-slate-700">Get Started</p>
                    <p className="text-[10px] text-slate-500">We begin your project right away</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Payment Calculator</p>
                <div className="rounded-xl overflow-hidden border border-slate-200">
                  <iframe src="https://wisetack.us/#/payment_calculator?type=HS" title="Payment Calculator" width="100%" height="600" frameBorder="0" className="w-full" style={{ minHeight: 500 }} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== FLAT PRICING (non-template quotes) ===== */}
        {!hasPagePricing && (
          <>
            <Card>
              <CardHeader><CardTitle>Pricing Summary</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg" data-testid="base-pricing">
                  <p className="font-semibold text-slate-700 mb-3">Base Package</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-600">
                      <span>Subtotal</span>
                      <span>${data.quote.pricing.base.subtotal.toLocaleString()}</span>
                    </div>
                    {data.quote.pricing.base.discount > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Discount</span>
                        <span>-${data.quote.pricing.base.discount.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-slate-800 pt-2 border-t border-slate-200">
                      <span>Total</span>
                      <span data-testid="base-total">${data.quote.pricing.base.total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {data.quote.pricing.addons.length > 0 && (
                  <div>
                    <p className="font-semibold text-slate-700 mb-3">Optional Add-ons</p>
                    <p className="text-sm text-slate-500 mb-3">Select any options you&apos;d like to include:</p>
                    <div className="space-y-3">
                      {data.quote.pricing.addons.map(addon => (
                        <div
                          key={addon.id}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedAddons[addon.id] ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300"
                          } ${alreadySigned ? "pointer-events-none opacity-70" : ""}`}
                          onClick={() => !alreadySigned && toggleAddon(addon.id)}
                          data-testid={`portal-addon-${addon.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              selectedAddons[addon.id] ? "bg-[#0F4C81] border-[#0F4C81]" : "border-slate-300"
                            }`}>
                              {selectedAddons[addon.id] && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-700">{addon.name}</p>
                              {addon.description && <p className="text-sm text-slate-500">{addon.description}</p>}
                            </div>
                            <p className="font-semibold text-[#0F4C81]">${addon.total.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Grand Total — only show once the customer has actually selected an
            option. Before any selection the figure would be misleading (just
            structure costs) and visually anchor the customer to a number that
            isn't really "their total". */}
        {Object.values(structureSelections).some(Boolean) && (
          <Card className="bg-gradient-to-br from-[#0F4C81] to-[#0D4270] text-white">
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-blue-200 text-xs uppercase tracking-wider">Total Due</p>
                <p className="text-4xl font-extrabold mt-1" data-testid="grand-total">${totals.total}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financing pages are rendered in-order within the sequential loop above */}

        {/* ===== DOWNLOAD PDF (prominent) ===== */}
        <div className="flex justify-center">
          <a
            href={`${API_URL}/public/quotes/${quoteId}/pdf`}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-white border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white font-semibold px-6 py-3 text-sm shadow-sm transition-colors"
            data-testid="portal-download-pdf-large"
          >
            <Download className="w-5 h-5" />
            Download PDF copy
          </a>
        </div>

        {/* ===== REVIEW & SIGN ===== */}
        <Card ref={signatureCardRef} data-testid="portal-signature-section">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              {alreadySigned ? "Signatures" : "Review & Sign"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            {alreadySigned ? (
              <div className="space-y-6">
                {/* Signed confirmation message */}
                {(() => {
                  const sigPage = (quote.pages || []).find(p => p.page_type === "signature");
                  const signedMsg = sigPage?.content?.signed_message;
                  if (signedMsg) {
                    const customerName = quote.customer_signature?.name || contact.name || "valued customer";
                    const firstName = customerName.split(" ")[0];
                    const renderedMsg = (typeof signedMsg === "string" ? signedMsg : "")
                      .replace(/\{\{?customer_name\}?\}/gi, customerName)
                      .replace(/\{\{?first_name\}?\}/gi, firstName)
                      .replace(/\{\{?company_name\}?\}/gi, company.name || "")
                      .replace(/\{\{?quote_number\}?\}/gi, quote.quote_number || "");
                    return (
                      <div className="text-center py-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4">
                        <p className="text-emerald-800 font-medium" data-testid="signed-message">{renderedMsg}</p>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Deposit state card. Driven entirely by the backend's
                    `deposit.state`, so reopening the link later always shows
                    the right thing rather than only working right after
                    signing. Reuses the existing /i/{code} pay page. */}
                {(() => {
                  const dep = data?.deposit;
                  if (!dep || dep.state === "none") return null;
                  const fname = dep.first_name || "there";
                  const phone = dep.support_phone || "843-252-4610";
                  const money = Number(dep.amount || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

                  if (dep.state === "awaiting_countersign") {
                    return (
                      <div className="rounded-xl border border-[#0F4C81]/20 bg-[#0F4C81]/5 px-5 py-5" data-testid="deposit-state-awaiting">
                        <h3 className="text-lg font-bold text-[#0F4C81]">Thanks, {fname}. You're signed.</h3>
                        <p className="text-sm text-slate-700 mt-1.5">
                          {justSigned
                            ? "We're confirming your quote now. Your deposit link will show up right here in a moment."
                            : <>We're reviewing your quote. Your deposit link will show up on this page once we confirm it. Questions? Call/Text <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="font-semibold text-[#0F4C81] underline">{phone}</a>.</>}
                        </p>
                      </div>
                    );
                  }

                  if (dep.state === "unpaid") {
                    return (
                      <div className="rounded-xl border border-[#0F4C81]/25 bg-white shadow-sm px-5 py-5" data-testid="deposit-state-unpaid">
                        <h3 className="text-lg font-bold text-[#0F4C81]">You're all set, {fname}.</h3>
                        <p className="text-sm text-slate-700 mt-1.5">
                          Your deposit is <strong>{money}</strong>. Pay it here and we'll get you on the schedule.
                        </p>
                        <a
                          href={dep.pay_path}
                          className="mt-4 inline-flex items-center justify-center w-full sm:w-auto px-7 py-3 rounded-lg bg-[#0F4C81] text-white font-bold hover:bg-[#0D4270] transition-colors"
                          data-testid="deposit-pay-btn"
                        >
                          Pay deposit
                        </a>
                        <p className="text-xs text-slate-500 mt-2.5" data-testid="deposit-alt-payment">
                          Paying by check or Zelle? Call/Text{" "}
                          <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="font-semibold text-slate-700 underline">{phone}</a>{" "}
                          and we'll mark it.
                          {data?.card_fee?.enabled && <> Bank transfer has no fee. Credit cards add a {data.card_fee.pct}% processing fee.</>}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-5" data-testid="deposit-state-paid">
                      <h3 className="text-lg font-bold text-emerald-800">Deposit received. Thank you.</h3>
                      <p className="text-sm text-emerald-900/80 mt-1.5">We'll reach out to get you scheduled.</p>
                    </div>
                  );
                })()}


                {/* Signed Summary — line items + total the customer approved.
                    Always visible in the signed state so the customer, the
                    admin, and any support person can see exactly what was
                    agreed to right next to the signature. */}
                {(() => {
                  const items = Array.isArray(quote.approved_breakdown) ? quote.approved_breakdown : [];
                  const approvedTotal = Number(quote.approved_total ?? quote.total ?? 0);
                  if (!items.length && !approvedTotal) return null;
                  return (
                    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="signed-summary">
                      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                        <p className="text-xs uppercase tracking-wider font-bold text-[#0F4C81]">Signed Summary</p>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {items.map((row, i) => {
                          const kind = (row.kind || "").toLowerCase();
                          const isDiscount = kind === "discount";
                          const amt = Number(row.amount || 0);
                          const amtStr = isDiscount
                            ? `-$${Math.abs(amt).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : `$${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                          const label = row.label || (isDiscount ? "Discount" : kind === "addon" ? "Add-on" : "Option");
                          const badge = isDiscount ? "Discount" : kind === "addon" ? (row.mandatory ? "Add-on • Required" : "Add-on") : "Option";
                          return (
                            <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`signed-summary-row-${i}`}>
                              <div className="min-w-0 pr-3">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{badge}</p>
                                <p className="text-slate-800 truncate">{label}</p>
                              </div>
                              <span className={`font-semibold whitespace-nowrap ${isDiscount ? "text-emerald-600" : "text-[#0F4C81]"}`}>{amtStr}</span>
                            </div>
                          );
                        })}
                        <div className="flex items-center justify-between px-4 py-3 bg-[#0F4C81]/5">
                          <span className="text-xs uppercase tracking-wider font-bold text-slate-700">Approved Total</span>
                          <span className="text-lg font-extrabold text-[#0F4C81]" data-testid="signed-summary-total">
                            ${approvedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Customer Signature */}
                <div className="text-center py-4">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-800 mb-1" data-testid="signed-confirmation">Customer Approved</h3>
                  {quote.customer_signature?.name && (
                    <p className="text-sm text-slate-500">
                      Signed by <strong>{quote.customer_signature.name}</strong>
                      {quote.customer_signature.signed_at && (
                        <> on {new Date(quote.customer_signature.signed_at).toLocaleDateString()}</>
                      )}
                    </p>
                  )}
                  {quote.customer_signature?.signature && (
                    <div className="mt-3 border border-slate-200 rounded-lg p-4 bg-slate-50 inline-block">
                      <img src={quote.customer_signature.signature} alt="Customer Signature" className="max-h-16" />
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-slate-200" />

                {/* Admin / Company Signature */}
                <div className="text-center py-4">
                  {quote.admin_signature?.signature ? (
                    <>
                      <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                      <h3 className="text-lg font-bold text-slate-800 mb-1" data-testid="admin-signed-confirmation">Company Approved</h3>
                      <p className="text-sm text-slate-500">
                        Signed by <strong>{quote.admin_signature.name}</strong>
                        {quote.admin_signature.signed_at && (
                          <> on {new Date(quote.admin_signature.signed_at).toLocaleDateString()}</>
                        )}
                      </p>
                      <div className="mt-3 border border-slate-200 rounded-lg p-4 bg-slate-50 inline-block">
                        <img src={quote.admin_signature.signature} alt="Company Signature" className="max-h-16" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                        <PenTool className="w-6 h-6 text-amber-600" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800 mb-1" data-testid="admin-sig-pending">Company Signature Pending</h3>
                      <p className="text-sm text-slate-500">
                        {company.name} will review and counter-sign this agreement.
                      </p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Step 1: Review Selections — live checkboxes */}
                {hasPagePricing && (
                  <div>
                    <p className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">1. Review Your Selections</p>
                    <div className="space-y-2">
                      {structureGroups.map((group, gi) => {
                        const structureKey = group.structure?.id || group.options[0]?.id || `default_${gi}`;
                        const structureTitle = group.structure?.content?.title || "Option";
                        const sharedItems2 = group.structure?.content?.line_items || [];
                        const sharedTotal2 = sharedItems2.reduce((sum2, it2) => sum2 + calcItemTotal(it2), 0);
                        const profitPct = parseFloat(quote.profit_margin_pct) || 0;
                        const taxPct = parseFloat(quote.vendor_tax_pct) || 0;
                        const pricingMode = quote.pricing_mode === "markup" ? "markup" : "margin";
                        return (
                          <div key={structureKey} className="border border-slate-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{structureTitle}</p>
                            <div className="space-y-1.5">
                              {group.options.map(opt => {
                                const c2 = opt.content || {};
                                const isOptSelected = structureSelections[structureKey] === opt.id;
                                const oItems = c2.line_items || [];
                                const oManual = parseFloat(c2.base_price || c2.manual_price) || 0;
                                let oTotal;
                                if (oManual > 0) {
                                  const optMkp = parseFloat(c2.price_markup_pct) || 0;
                                  const optAdj = parseFloat(c2.price_adjustment) || 0;
                                  oTotal = oManual + (oManual * optMkp / 100) + optAdj;
                                } else {
                                  const oSub = oItems.length > 0 ? oItems.reduce((s, it2) => s + calcItemTotal(it2), 0) : 0;
                                  const totalRaw = oSub + sharedTotal2;
                                  const withMargin = applyProfit(totalRaw, profitPct, pricingMode);
                                  const optTax = totalRaw * (taxPct / 100);
                                  const optBase = withMargin + optTax;
                                  const optMkp = parseFloat(c2.price_markup_pct) || 0;
                                  const optAdj = parseFloat(c2.price_adjustment) || 0;
                                  oTotal = optBase + (optBase * optMkp / 100) + optAdj;
                                }
                                return (
                                  <div key={opt.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all text-sm ${isOptSelected ? "bg-blue-50 border border-[#0F4C81]/30" : "hover:bg-slate-50"}`}
                                    onClick={() => handleStructureSelection(structureKey, opt.id)}
                                  >
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isOptSelected ? "border-[#0F4C81]" : "border-slate-300"}`}>
                                      {isOptSelected && <div className="w-2 h-2 rounded-full bg-[#0F4C81]" />}
                                    </div>
                                    <span className={`flex-1 ${isOptSelected ? "font-semibold text-slate-800" : "text-slate-600"}`}>{c2.option_name || opt.title}</span>
                                    <span className={`font-bold ${isOptSelected ? "text-[#0F4C81]" : "text-slate-400"}`}>${oTotal.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      {addonPages.length > 0 && (
                        <div className="border border-slate-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Add-Ons</p>
                          <div className="space-y-1.5">
                            {addonPages.map(addon => {
                              const c2 = addon.content || {};
                              const isAddonSel = !!selectedAddons[addon.id];
                              const aItems = c2.line_items || [];
                              const aManual = parseFloat(c2.base_price || c2.manual_price) || 0;
                              const addonMkp = parseFloat(c2.price_markup_pct) || 0;
                              const addonAdj = parseFloat(c2.price_adjustment) || 0;
                              let aTotal;
                              if (aManual > 0) {
                                aTotal = aManual + (aManual * addonMkp / 100) + addonAdj;
                              } else {
                                // Add-ons never inherit the quote-level margin.
                                const aSub = aItems.length > 0 ? aItems.reduce((s, it2) => s + calcItemTotal(it2), 0) : 0;
                                const addonTaxPct = parseFloat(quote.vendor_tax_pct) || 0;
                                const addonTax = aSub * (addonTaxPct / 100);
                                const addonBase = aSub + addonTax;
                                aTotal = addonBase + (addonBase * addonMkp / 100) + addonAdj;
                              }
                              return (
                                <div key={addon.id}
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all text-sm ${isAddonSel ? "bg-emerald-50 border border-emerald-300" : "hover:bg-slate-50"}`}
                                  onClick={() => toggleAddon(addon.id)}
                                >
                                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${isAddonSel ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
                                    {isAddonSel && <Check className="w-3 h-3 text-white" />}
                                  </div>
                                  <span className={`flex-1 ${isAddonSel ? "font-semibold text-slate-800" : "text-slate-600"}`}>{c2.addon_name || addon.title}</span>
                                  <span className={`font-bold ${isAddonSel ? "text-emerald-700" : "text-slate-400"}`}>+${aTotal.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Live total — show one row per discount when present */}
                    {Array.isArray(totals.discount_rows) && totals.discount_rows.length > 0 ? (
                      <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-1.5">
                        {totals.discount_rows.map((d, i) => (
                          <div key={i} className="flex items-center justify-between text-rose-700">
                            <span className="font-medium text-sm">{d.label}</span>
                            <span className="text-base font-bold" data-testid={`review-discount-${i}`}>−${d.removed.toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                          </div>
                        ))}
                      </div>
                    ) : parseFloat(totals.discount) > 0 && (
                      <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-center justify-between text-rose-700">
                        <span className="font-medium text-sm">{totals.discount_label || "Discount"}</span>
                        <span className="text-lg font-bold" data-testid="review-discount">−${parseFloat(totals.discount).toLocaleString("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                    )}
                    <div className="mt-3 bg-[#0F4C81] rounded-lg p-3 flex items-center justify-between text-white">
                      <span className="font-medium text-sm">Your Total</span>
                      <span className="text-xl font-extrabold" data-testid="review-total">${totals.total}</span>
                    </div>
                  </div>
                )}

                {/* Step 2: Terms & Conditions */}
                {(() => {
                  const sigPage = (quote.pages || []).find(p => p.page_type === "signature");
                  const textPage = (quote.pages || []).find(p => p.page_type === "text");
                  const terms = sigPage?.content?.terms_and_conditions || textPage?.content?.body || "";
                  if (!terms) return null;
                  return (
                    <div>
                      <p className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">2. Terms & Conditions</p>
                      <div className="border border-slate-200 rounded-lg p-3 sm:p-4 max-h-48 overflow-y-auto bg-slate-50 text-sm text-slate-600">
                        <RichTextContent content={terms} className="leading-relaxed" />
                      </div>
                    </div>
                  );
                })()}

                {/* Step 2.5: Customer Questions */}
                {(() => {
                  const questions = Array.isArray(quote.signing_questions) ? quote.signing_questions : [];
                  if (!questions.length) return null;
                  // Determine the selected option's name/tag so brand-specific
                  // help links (e.g. shingle color charts) only show when relevant.
                  const selOptId = Object.values(structureSelections).find(Boolean);
                  const selOption = (quote.pages || []).find(p => p.id === selOptId);
                  const selOptName = (selOption?.content?.option_name || "").toLowerCase();
                  const selOptTag = (selOption?.content?.option_tag || "").toLowerCase();
                  const optionContext = `${selOptName} ${selOptTag}`.toLowerCase();
                  return (
                    <div data-testid="signing-questions-block">
                      <p className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">{hasPagePricing ? "3" : "2"}. A Few Questions</p>
                      <div className="space-y-4">
                        {questions.map((q) => {
                          const val = customerAnswers[q.id] ?? "";
                          const setVal = (v) => setCustomerAnswers(prev => ({ ...prev, [q.id]: v }));
                          const matchingLinks = (q.dynamic_links || []).filter(dl => {
                            // No match string → always show
                            if (!dl.match) return true;
                            // No option selected yet → show every link as a fallback
                            // so customers can browse colors before picking
                            if (!optionContext.trim()) return true;
                            const tokens = String(dl.match).toLowerCase().split("|").map(s => s.trim()).filter(Boolean);
                            return tokens.some(t => optionContext.includes(t));
                          });
                          return (
                            <div key={q.id} className="space-y-1.5" data-testid={`signing-question-${q.id}`}>
                              <label className="text-sm font-medium text-slate-700 block">
                                {q.label}{q.required && <span className="text-rose-500 ml-1">*</span>}
                              </label>
                              {q.help_text && <p className="text-xs text-slate-500 italic">{q.help_text}</p>}
                              {q.type === "textarea" ? (
                                <textarea
                                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm min-h-[72px] focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/40 focus:border-[#0F4C81]"
                                  placeholder={q.placeholder || "Your answer…"}
                                  value={val}
                                  onChange={(e) => setVal(e.target.value)}
                                  disabled={alreadySigned}
                                  data-testid={`signing-answer-${q.id}`}
                                />
                              ) : q.type === "choice" ? (
                                <div className="flex flex-wrap gap-2">
                                  {(q.options || []).map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => setVal(opt)}
                                      disabled={alreadySigned}
                                      className={`px-3 py-1.5 rounded-md border text-sm font-medium ${val === opt ? "border-[#0F4C81] bg-[#0F4C81] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
                                      data-testid={`signing-answer-${q.id}-${opt}`}
                                    >{opt}</button>
                                  ))}
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/40 focus:border-[#0F4C81]"
                                  placeholder={q.placeholder || "Your answer…"}
                                  value={val}
                                  onChange={(e) => setVal(e.target.value)}
                                  disabled={alreadySigned}
                                  data-testid={`signing-answer-${q.id}`}
                                />
                              )}
                              {matchingLinks.map((dl, i) => (
                                <div key={i} className="pt-1">
                                  <a
                                    href={dl.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-[#0F4C81] hover:underline inline-flex items-center gap-1"
                                    data-testid={`signing-help-link-${q.id}-${i}`}
                                  >
                                    🎨 {dl.label || "Browse colors"} ↗
                                  </a>
                                  {dl.note && <p className="text-[11px] text-amber-700 mt-1 italic">{dl.note}</p>}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Step 3: Signature */}
                <div>
                  {(() => {
                    const hasQs = Array.isArray(quote.signing_questions) && quote.signing_questions.length > 0;
                    const step = (hasPagePricing ? 3 : 2) + (hasQs ? 1 : 0);
                    return <p className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">{step}. Sign Below</p>;
                  })()}
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden mb-3">
                    <button
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${signatureMethod === "type" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600"}`}
                      onClick={() => setSignatureMethod("type")}
                      data-testid="portal-sig-type"
                    >Type Name</button>
                    <button
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${signatureMethod === "draw" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600"}`}
                      onClick={() => setSignatureMethod("draw")}
                      data-testid="portal-sig-draw"
                    >Draw</button>
                  </div>

                {signatureMethod === "type" ? (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Type your full name to sign</Label>
                      <Input
                        value={typedSignature}
                        onChange={(e) => { setTypedSignature(e.target.value); setSignerName(e.target.value); }}
                        placeholder="Your full name"
                        className="h-12 text-lg"
                        data-testid="portal-type-sig"
                      />
                    </div>
                    {typedSignature && (
                      <div className="border-2 border-slate-200 rounded-lg p-6 bg-white text-center">
                        <p className="text-3xl italic text-[#0F4C81]" style={{ fontFamily: "Georgia, serif" }}>{typedSignature}</p>
                        <div className="border-t border-slate-300 mt-4 pt-2">
                          <p className="text-xs text-slate-400">Digital Signature</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                      <canvas
                        ref={canvasRef}
                        width={400}
                        height={150}
                        className="w-full cursor-crosshair bg-white touch-none"
                        onMouseDown={startDraw}
                        onMouseMove={onDraw}
                        onMouseUp={stopDraw}
                        onMouseLeave={stopDraw}
                        onTouchStart={startDraw}
                        onTouchMove={onDraw}
                        onTouchEnd={stopDraw}
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={clearCanvas}>Clear</Button>
                    <div className="space-y-2">
                      <Label>Printed Name</Label>
                      <Input
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        placeholder="Your full name"
                        className="h-12"
                        data-testid="portal-draw-name"
                      />
                    </div>
                  </div>
                )}

                <div className="p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
                  By signing, you approve this proposal for a total of <strong>${totals.total}</strong>
                  {Object.values(selectedAddons).some(v => v) && " (including selected add-ons)"}.
                </div>

                {(() => {
                  const questions = Array.isArray(quote.signing_questions) ? quote.signing_questions : [];
                  const missingRequired = questions.filter(q => {
                    if (!q.required) return false;
                    const v = customerAnswers[q.id];
                    return v == null || (typeof v === "string" && !v.trim());
                  });
                  const blocked = missingRequired.length > 0;
                  return (
                    <>
                      {blocked && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs px-3 py-2" data-testid="missing-required-warning">
                          Please answer the required questions above before signing
                          {missingRequired.length > 0 && `: ${missingRequired.map(q => q.label).join(", ")}`}.
                        </div>
                      )}
                      <Button
                        className="w-full h-12 bg-[#0F4C81] hover:bg-[#0D4270] text-white"
                        onClick={handleSign}
                        disabled={blocked || signing || (signatureMethod === "type" ? !typedSignature : !signerName)}
                        data-testid="portal-sign-btn"
                      >
                        {signing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <PenTool className="w-5 h-5 mr-2" />}
                        Sign & Approve Quote
                      </Button>
                    </>
                  );
                })()}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Valid Until */}
        {quote.valid_until && (
          <p className="text-center text-sm text-slate-400 pb-6">
            This quote is valid until {new Date(quote.valid_until).toLocaleDateString()}
          </p>
        )}

        {/* Footer */}
        <div className="text-center pb-8 text-sm text-slate-400">
          <p className="font-semibold text-slate-500 uppercase tracking-wider text-[11px]">Protect. Secure. Transform.</p>
          <p className="mt-2">{company.name}</p>
          {company.address && <p>{company.address}</p>}
        </div>
      </div>

      {/* Fixed-bottom mobile Sign CTA — brand's first-impression call to action.
          Visible only on <sm when the quote is not yet fully signed. Tapping
          scrolls the signature card into view. Respects iOS home-indicator
          safe area via env(safe-area-inset-bottom). */}
      {!alreadySigned && (
        <div
          className="sm:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur shadow-[0_-4px_16px_rgba(15,76,129,0.08)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="portal-mobile-sign-bar"
        >
          <div className="px-4 py-3">
            <Button
              onClick={() => signatureCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="w-full h-12 text-base bg-[#0F4C81] hover:bg-[#0D4270] text-white font-semibold"
              data-testid="portal-mobile-sign-scroll-btn"
            >
              <PenTool className="w-5 h-5 mr-2" />
              Sign & Approve Quote
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
